import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, TrendingUp, DollarSign, Percent, Clock,
  Pencil, X, Plus, Calendar, FileText, Download, Trash2, AlertTriangle, Loader2, Target, Scale, ArrowRight,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api, { errorText } from '../config';
import { StageBadge, stageInfo, STAGES } from './Portfolio';

export default function PortcoDetail() {
  const { portcoId } = useParams();
  const navigate = useNavigate();
  const [portco, setPortco] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuarterlyForm, setShowQuarterlyForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);   // dry-run result from API
  const [deleting, setDeleting] = useState(false);
  const [exitReadiness, setExitReadiness] = useState(null);
  const [normHistory, setNormHistory] = useState([]);
  const [deckQuarter, setDeckQuarter] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}`);
  const [deckBusy, setDeckBusy] = useState(false);
  const [deckError, setDeckError] = useState(null);

  const fetchPortco = useCallback(async () => {
    setLoading(true);
    try {
      const [portcoRes, reportsRes] = await Promise.all([
        api.get(`/api/portfolio/companies/${portcoId}`).catch(() => null),
        api.get('/api/reports').catch(() => null),
      ]);
      setPortco(portcoRes?.data);
      if (reportsRes?.data) {
        setReports(reportsRes.data.filter(r => r.portco_id === portcoId));
      }
    } catch {
      setPortco(null);
    } finally {
      setLoading(false);
    }
  }, [portcoId]);

  useEffect(() => {
    fetchPortco();
  }, [fetchPortco]);

  useEffect(() => {
    api.get(`/api/normalization/portco/${portcoId}/history`)
      .then(r => setNormHistory(r.data || []))
      .catch(() => setNormHistory([]));
  }, [portcoId]);

  const fetchExitReadiness = useCallback(async () => {
    try {
      const res = await api.get(`/api/portfolio/companies/${portcoId}/exit-readiness`);
      setExitReadiness(res.data);
    } catch {
      setExitReadiness(null);
    }
  }, [portcoId]);

  useEffect(() => {
    if (portco?.stage === 'pre_exit' && !exitReadiness) fetchExitReadiness();
  }, [portco?.stage, exitReadiness, fetchExitReadiness]);

  const handleQuarterlySave = async (formData) => {
    try {
      await api.post(`/api/portfolio/companies/${portcoId}/quarterly`, formData);
      setShowQuarterlyForm(false);
      setExitReadiness(null);  // re-assess with the new data
      await fetchPortco();
    } catch (err) {
      console.error('Quarterly save failed:', err);
    }
  };

  const handleEditSave = async (formData) => {
    try {
      await api.put(`/api/portfolio/companies/${portcoId}`, formData);
      setShowEditForm(false);
      setExitReadiness(null);
      await fetchPortco();
    } catch (err) {
      console.error('Edit failed:', err);
    }
  };

  // Step 1: ask the API what a deletion would remove — shows the warning modal
  const handleDeleteClick = async () => {
    try {
      const res = await api.delete(`/api/portfolio/companies/${portcoId}`);
      setDeletePreview(res.data);
    } catch (err) {
      console.error('Delete preview failed:', err);
    }
  };

  // Step 2: user confirmed — permanently delete and clean up
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/portfolio/companies/${portcoId}?confirm=true`);
      navigate('/portfolio');
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleting(false);
      setDeletePreview(null);
    }
  };

  // Board deck — generated per portfolio company only, from that company's
  // own reported data (quarterly figures, normalized periods, its alerts).
  const handleGenerateDeck = async () => {
    setDeckBusy(true); setDeckError(null);
    try {
      const res = await api.post('/api/reports/board-deck', { quarter: deckQuarter, portco_id: portcoId });
      if (res?.data?.report_id) navigate(`/reports?report=${res.data.report_id}`);
      else setDeckError('Deck generated but could not be opened.');
    } catch (err) {
      setDeckError(errorText(err, 'Board deck generation failed.'));
    } finally { setDeckBusy(false); }
  };

  const handleReportDownload = async (reportId) => {
    try {
      const res = await api.get(`/api/reports/${reportId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Caply_Report_${reportId}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(`${api.defaults.baseURL}/api/reports/${reportId}/download`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!portco) {
    return (
      <div className="page-container">
        <button onClick={() => navigate('/portfolio')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 transition mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Portfolio
        </button>
        <p className="text-slate-500">Portfolio company not found.</p>
      </div>
    );
  }

  const fmtM = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${(v / 1e3).toFixed(0)}K`;
  const quarters = portco.quarterly_metrics || [];

  return (
    <div className="page-container max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/portfolio')}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 transition mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Portfolio
      </button>

      {/* Header card */}
      <div className="card">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-navy-700 to-navy-900 flex items-center justify-center text-white text-xl font-bold">
              {portco.portco_name.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy-900">{portco.portco_name}</h2>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="badge badge-slate">{portco.industry}</span>
                <StageBadge stage={portco.stage} />
                <span className="badge badge-blue">{portco.geography}</span>
                {portco.status === 'exited' && <span className="badge badge-red">Exited</span>}
                {portco.deal_origin && <span className="badge badge-green text-xs">Converted from deal</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEditForm(true)}
            className="btn-secondary text-sm"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={handleDeleteClick}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg text-red-600 border border-red-200 hover:bg-red-50 transition"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
        </div>

        {portco.description && (
          <p className="text-sm text-slate-600 mt-4 leading-relaxed">{portco.description}</p>
        )}

        {/* Key metrics grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">Revenue</span>
            </div>
            <p className="text-lg font-bold font-mono text-navy-900">{fmtM(portco.revenue)}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">EBITDA</span>
            </div>
            <p className="text-lg font-bold font-mono text-emerald-600">{fmtM(portco.ebitda)}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Percent className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">Margin</span>
            </div>
            <p className="text-lg font-bold font-mono text-slate-700">{((portco.ebitda_margin || 0) * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">Invested</span>
            </div>
            <p className="text-lg font-bold font-mono text-navy-900">{fmtM(portco.initial_investment)}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">MOIC</span>
            </div>
            <p className="text-lg font-bold font-mono text-gold-600">{portco.moic?.toFixed(2) || '0'}x</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Percent className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">IRR</span>
            </div>
            <p className="text-lg font-bold font-mono text-navy-900">{portco.irr?.toFixed(1) || '0'}%</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">Hold Period</span>
            </div>
            <p className="text-lg font-bold font-mono text-navy-900">{portco.hold_period_years?.toFixed(1) || '0'} yrs</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">Acquired</span>
            </div>
            <p className="text-sm font-bold font-mono text-navy-900">{portco.acquisition_date}</p>
          </div>
        </div>

        {portco.lead_partner && (
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-500">
            <Building2 className="w-4 h-4 text-brand-600" />
            Lead Partner: <span className="font-medium text-navy-900">{portco.lead_partner}</span>
          </div>
        )}
      </div>

      {/* Quarterly Financials */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="card-title">
            <TrendingUp className="w-5 h-5 text-brand-600" />
            Quarterly Financials
          </h3>
          <button
            onClick={() => setShowQuarterlyForm(true)}
            className="btn-primary text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Quarter
          </button>
        </div>

        {quarters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2 pr-4">Period</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2 pr-4">Revenue</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2 pr-4">EBITDA</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2 pr-4">Margin</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {quarters.sort((a, b) => a.period.localeCompare(b.period)).map((q, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition">
                    <td className="py-3 pr-4 font-medium text-navy-900">{q.period}</td>
                    <td className="py-3 pr-4 text-right font-mono text-navy-900">{fmtM(q.revenue)}</td>
                    <td className="py-3 pr-4 text-right font-mono text-emerald-600">{fmtM(q.ebitda)}</td>
                    <td className="py-3 pr-4 text-right font-mono text-slate-600">{((q.ebitda_margin || 0) * 100).toFixed(1)}%</td>
                    <td className="py-3 text-slate-500 text-xs">{q.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No quarterly metrics reported yet.</p>
            <p className="text-xs mt-1">Add quarterly revenue and EBITDA to track performance over time.</p>
          </div>
        )}
      </div>

      {/* Exit Readiness (Pre-Exit stage) */}
      {portco.stage === 'pre_exit' && (
        <div className="card">
          <h3 className="card-title mb-1">
            <Target className="w-5 h-5 text-amber-500" />
            Exit Readiness
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Computed only from data your firm has entered — no market benchmarks. Categories marked
            'insufficient data' need real submissions before Caply will score them.
          </p>
          {!exitReadiness && <p className="text-sm text-slate-400">Loading readiness assessment…</p>}
          {exitReadiness && (
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
                <div className="text-center">
                  <p className="text-3xl font-bold text-navy-900">
                    {exitReadiness.score === null ? '—' : `${exitReadiness.score}`}
                    {exitReadiness.score !== null && <span className="text-base text-slate-400 font-normal">/100</span>}
                  </p>
                  <p className="text-xs text-slate-500">Readiness score</p>
                </div>
                <div className="flex-1">
                  {exitReadiness.blockers.length > 0 ? (
                    <p className="text-sm text-red-600">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {exitReadiness.blockers.length} blocker(s): {exitReadiness.blockers.join(', ')}
                    </p>
                  ) : (
                    <p className="text-sm text-emerald-600">No blockers among the scored categories.</p>
                  )}
                </div>
              </div>
              {exitReadiness.categories.map(c => (
                <div key={c.key} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-navy-900">{c.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.detail}</p>
                  </div>
                  <span className={`badge ${
                    c.status === 'on_track' ? 'bg-emerald-100 text-emerald-700' :
                    c.status === 'watch' ? 'bg-amber-100 text-amber-700' :
                    c.status === 'gap' ? 'bg-red-100 text-red-700' : 'badge-slate'
                  }`}>
                    {c.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Normalized Financials (from the Normalization page) */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="card-title">
            <Scale className="w-5 h-5 text-brand-600" />
            Normalized Financials
          </h3>
          <button
            onClick={() => navigate('/normalization')}
            className="text-sm text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"
          >
            Normalize a period <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {normHistory.length === 0 ? (
          <p className="text-sm text-slate-400">
            No normalized periods yet — upload this company's P&L CSV on the Normalization page and its standardized financials will chart here.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={[...normHistory].reverse().map(r => ({
                    period: r.period,
                    revenue: r.total_revenue,
                    ebitda: r.ebitda,
                  }))}
                  margin={{ left: -10, right: 10, top: 5, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tickFormatter={fmtM} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} formatter={(v) => fmtM(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#a67f3d" strokeWidth={2.5} dot={{ r: 4, fill: '#a67f3d' }} name="Revenue" />
                  <Line type="monotone" dataKey="ebitda" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, fill: '#059669' }} name="EBITDA" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Periods (newest first)</p>
              <div className="space-y-1.5">
                {normHistory.map(r => {
                  const addBacks = (r.non_recurring_items || []).reduce((a, b) => a + (b.amount || 0), 0);
                  return (
                    <div key={r.id || r.period} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5 text-sm">
                      <div>
                        <span className="font-medium text-navy-900">{r.period}</span>
                        {r.created_by && <span className="text-xs text-slate-400 ml-2">by {r.created_by}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-slate-500">
                        <span>Rev <b className="font-mono text-navy-900">{fmtM(r.total_revenue)}</b></span>
                        <span>EBITDA <b className="font-mono text-emerald-700">{fmtM(r.ebitda)}</b></span>
                        <span>margin <b className="font-mono">{r.ebitda_margin_pct}%</b></span>
                        {addBacks > 0 && <span className="text-amber-600">+{fmtM(addBacks)} add-backs</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Associated Reports */}
      {reports.length > 0 && (
        <div className="card">
          <h3 className="card-title mb-4">
            <FileText className="w-5 h-5 text-brand-600" />
            Reports ({reports.length})
          </h3>
          <div className="space-y-2">
            {reports.map(r => (
              <div key={r.report_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-navy-900">{r.report_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  <p className="text-xs text-slate-500">{r.report_id} · {r.period || 'N/A'}</p>
                </div>
                <button
                  onClick={() => handleReportDownload(r.report_id)}
                  className="text-brand-600 hover:text-brand-800 text-sm font-medium flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Board Deck — a function of THIS company only */}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="card-title">
            <FileText className="w-5 h-5 text-brand-600" />
            Board Deck
          </h3>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Assembles this company's board packet from its own reported data — quarterly figures,
          normalized periods, and its active alerts. Unreported values are marked
          &ldquo;not reported&rdquo; — nothing is estimated.
        </p>
        <div className="flex items-center gap-3 mt-4">
          <input
            type="text"
            className="form-input w-32"
            value={deckQuarter}
            onChange={e => setDeckQuarter(e.target.value)}
            placeholder="Q3 2026"
          />
          <button className="btn-primary flex items-center gap-2" onClick={handleGenerateDeck} disabled={deckBusy}>
            {deckBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {deckBusy ? 'Generating…' : 'Generate board deck'}
          </button>
          {deckError && <span className="text-xs text-rose-600">{deckError}</span>}
        </div>
      </div>

      {/* Modals */}
      {showQuarterlyForm && (
        <QuarterlyFormModal
          onSave={handleQuarterlySave}
          onClose={() => setShowQuarterlyForm(false)}
        />
      )}
      {showEditForm && (
        <EditPortcoModal
          portco={portco}
          onSave={handleEditSave}
          onClose={() => setShowEditForm(false)}
        />
      )}
      {deletePreview && (
        <DeleteWarningModal
          preview={deletePreview}
          portcoName={portco.portco_name}
          deleting={deleting}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeletePreview(null)}
        />
      )}
    </div>
  );
}

// ── Delete Warning Modal ──
// Content comes from the API's dry-run response, so the warning always
// matches exactly what the backend will clean up.
function DeleteWarningModal({ preview, portcoName, deleting, onConfirm, onClose }) {
  const willDelete = preview.will_delete || {};
  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={deleting ? undefined : onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="text-lg font-bold text-navy-900">Delete portfolio company?</h3>
          </div>
          {!deleting && (
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            {preview.warning}
          </p>
          <div className="bg-red-50 border border-red-100 rounded-lg p-4 space-y-2">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">What will be removed</p>
            <ul className="text-sm text-slate-700 space-y-1">
              <li>• Portfolio company <span className="font-semibold">{portcoName}</span></li>
              <li>• {willDelete.quarterly_metrics ?? 0} quarterly metric record(s)</li>
              <li>• {willDelete.generated_reports ?? 0} generated report(s)</li>
              <li>• All agent memories tied to this company</li>
              {preview.origin_deal_note && <li className="text-slate-500">{preview.origin_deal_note}</li>}
            </ul>
          </div>
          <p className="text-xs text-slate-500">This action cannot be undone.</p>
        </div>
        <div className="flex justify-end gap-2 p-5 pt-0">
          <button onClick={onClose} disabled={deleting} className="btn-secondary text-sm">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-60"
          >
            {deleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Yes, delete permanently
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quarterly Form Modal ──
function QuarterlyFormModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    period: `Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()}`,
    revenue: '',
    ebitda: '',
    notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      period: form.period,
      revenue: parseFloat(form.revenue) || 0,
      ebitda: parseFloat(form.ebitda) || 0,
      notes: form.notes || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-navy-900">Add Quarterly Metrics</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="form-label">Period *</label>
            <input
              className="form-input"
              required
              value={form.period}
              onChange={e => setForm({...form, period: e.target.value})}
              placeholder="e.g. Q3 2026"
            />
          </div>
          <div>
            <label className="form-label">Quarterly Revenue ($) *</label>
            <input
              className="form-input"
              type="number"
              required
              value={form.revenue}
              onChange={e => setForm({...form, revenue: e.target.value})}
              placeholder="e.g. 12000000"
            />
          </div>
          <div>
            <label className="form-label">Quarterly EBITDA ($) *</label>
            <input
              className="form-input"
              type="number"
              required
              value={form.ebitda}
              onChange={e => setForm({...form, ebitda: e.target.value})}
              placeholder="e.g. 3200000"
            />
          </div>
          <div>
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              rows={2}
              value={form.notes}
              onChange={e => setForm({...form, notes: e.target.value})}
              placeholder="Optional notes for this quarter..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1">Save Quarter</button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Portco Modal ──
function EditPortcoModal({ portco, onSave, onClose }) {
  const [form, setForm] = useState({
    portco_name: portco.portco_name || '',
    industry: portco.industry || 'Technology',
    geography: portco.geography || 'North America',
    description: portco.description || '',
    lead_partner: portco.lead_partner || '',
    initial_investment: portco.initial_investment || '',
    acquisition_date: portco.acquisition_date || '',
    revenue: portco.revenue || '',
    ebitda: portco.ebitda || '',
    moic: portco.moic || '1.0',
    irr: portco.irr || '0',
    stage: portco.stage || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      stage: form.stage || null,
      initial_investment: parseFloat(form.initial_investment) || 0,
      revenue: parseFloat(form.revenue) || 0,
      ebitda: parseFloat(form.ebitda) || 0,
      moic: parseFloat(form.moic) || 1.0,
      irr: parseFloat(form.irr) || 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-bold text-navy-900">Edit {portco.portco_name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="form-label">Company Name *</label>
            <input className="form-input" required value={form.portco_name} onChange={e => setForm({...form, portco_name: e.target.value})} />
          </div>
          <div>
            <label className="form-label">Stage</label>
            <select className="form-input" value={form.stage} onChange={e => setForm({...form, stage: e.target.value})}>
              <option value="">Auto-infer from financials</option>
              {STAGES.map(st => <option key={st.key} value={st.key}>{st.label} — {st.desc}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Industry</label>
              <select className="form-input" value={form.industry} onChange={e => setForm({...form, industry: e.target.value})}>
                <option>Technology</option>
                <option>Enterprise SaaS</option>
                <option>HealthTech</option>
                <option>Industrial Automation</option>
                <option>CleanTech SaaS</option>
                <option>Financial Services</option>
                <option>Consumer</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="form-label">Geography</label>
              <select className="form-input" value={form.geography} onChange={e => setForm({...form, geography: e.target.value})}>
                <option>North America</option>
                <option>Europe</option>
                <option>Asia Pacific</option>
                <option>Latin America</option>
                <option>Global</option>
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Initial Investment ($)</label>
              <input className="form-input" type="number" value={form.initial_investment} onChange={e => setForm({...form, initial_investment: e.target.value})} />
            </div>
            <div>
              <label className="form-label">Acquisition Date</label>
              <input className="form-input" type="date" value={form.acquisition_date} onChange={e => setForm({...form, acquisition_date: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Revenue ($)</label>
              <input className="form-input" type="number" value={form.revenue} onChange={e => setForm({...form, revenue: e.target.value})} />
            </div>
            <div>
              <label className="form-label">EBITDA ($)</label>
              <input className="form-input" type="number" value={form.ebitda} onChange={e => setForm({...form, ebitda: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">MOIC (x)</label>
              <input className="form-input" type="number" step="0.1" value={form.moic} onChange={e => setForm({...form, moic: e.target.value})} />
            </div>
            <div>
              <label className="form-label">IRR (%)</label>
              <input className="form-input" type="number" step="0.1" value={form.irr} onChange={e => setForm({...form, irr: e.target.value})} />
            </div>
          </div>
          <div>
            <label className="form-label">Lead Partner</label>
            <input className="form-input" value={form.lead_partner || ''} onChange={e => setForm({...form, lead_partner: e.target.value})} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1">Save Changes</button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
